import { toast } from "sonner";
import { Constants } from "../../../../utils/constants";
import { getApiCallArguments } from "./paramHelpers";
import logger from "../../../../utils/logger";


/**
 * Execute an API call - simplified version that works directly with the API service
 * @param {Object} endpoint - The endpoint to call
 * @param {Object} endpointParams - The parameters for the endpoint
 * @param {Object} options - Additional options with callbacks
 * @returns {Promise<Object>} The API response
 */
export const executeApiCall = async (endpoint, endpointParams, options) => {
  const { userId, baseUrl, apiService, setApiCallResults, setResponse, setLoading } = options;

  if (typeof setLoading === "function") {
    setLoading(true);
  }

  // Save original base URL outside try/catch so it's accessible in both blocks
  const originalBaseUrl = Constants.BASE_URL;

  try {
    // Set new base URL temporarily
    if (baseUrl) {
      Constants.BASE_URL = baseUrl;
      logger.log(`Temporarily setting API base URL to: ${baseUrl}`);
    }

    // Prepare arguments for the API call
    const args = getApiCallArguments(endpoint, endpointParams, userId);
    logger.log(`Calling ${endpoint.fnName} with:`, args);

    // Check if the method exists in the API service
    if (typeof apiService[endpoint.fnName] !== "function") {
      throw new Error(`API method '${endpoint.fnName}' not found`);
    }

    // Make the API call
    const response = await apiService[endpoint.fnName](...args);

    // Record the result
    const endpointKey = `${endpoint.method}-${endpoint.path}`;
    const statusCode = response?.status || 200;

    if (typeof setApiCallResults === "function") {
      setApiCallResults(prev => ({
        ...prev,
        [endpointKey]: { statusCode },
      }));
    }

    // Handle PDF downloads (special case for blob responses)
    if (response instanceof Blob) {
      // Create a download link
      const url = window.URL.createObjectURL(response);
      const link = document.createElement("a");
      link.href = url;

      // Set filename based on endpoint
      if (endpoint.fnName === "downloadInvoice") {
        link.download = `invoice-${endpointParams.token || "document"}.pdf`;
      } else if (endpoint.fnName === "downloadStatement") {
        link.download = `statement-${new Date().toISOString().split("T")[0]}.pdf`;
      } else {
        link.download = `document-${new Date().toISOString().split("T")[0]}.pdf`;
      }

      // Execute download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Create response object for display
      const customResponse = {
        status: 200,
        data: {
          message: `File ${link.download} downloaded successfully`,
        },
      };

      if (typeof setResponse === "function") {
        setResponse(customResponse);
      }

      // Show success toast
      toast.success(`Downloaded ${endpoint.name} successfully`);

      // Restore original base URL
      Constants.BASE_URL = originalBaseUrl;

      if (typeof setLoading === "function") {
        setLoading(false);
      }

      return customResponse;
    }

    // For regular responses - check if status code indicates an error (4xx or 5xx)
    if (typeof setResponse === "function") {
      setResponse(response);
    }

    // Check if the status code is an error code (4xx or 5xx)
    if (statusCode >= 400) {
      // Show error toast for 4xx and 5xx status codes
      toast.error(`${endpoint.name} failed (${statusCode}): ${response?.data?.message || "Request failed"}`);
    } else {
      // Only show success toast for successful status codes (2xx)
      toast.success(`${endpoint.name} successful (${statusCode})`);
    }

    // Restore original base URL
    Constants.BASE_URL = originalBaseUrl;

    return response;
  } catch (error) {
    logger.error(`Error executing ${endpoint.fnName}:`, error);

    // Record error in results
    const endpointKey = `${endpoint.method}-${endpoint.path}`;
    const statusCode = error.response?.status || 500;

    if (typeof setApiCallResults === "function") {
      setApiCallResults(prev => ({
        ...prev,
        [endpointKey]: { statusCode },
      }));
    }

    // Create error response
    const errorResponse = {
      error: true,
      message: error.message || "An error occurred",
      details: error.response?.data,
      statusCode,
    };

    if (typeof setResponse === "function") {
      setResponse(errorResponse);
    }

    // Show error toast
    toast.error(`Error: ${error.message || "Failed to execute API call"}`);

    // Restore original base URL if it was changed
    if (baseUrl) {
      Constants.BASE_URL = originalBaseUrl;
    }

    return errorResponse;
  } finally {
    // Always reset loading state
    if (typeof setLoading === "function") {
      setLoading(false);
    }
  }
};
